import { ExcludedPlugins } from 'owa-editor-plugins-list';
import switchToReadWriteRecipient from 'owa-mail-compose-actions/lib/actions/switchToReadWriteRecipient';
import canModifyRecipients from 'owa-mail-compose-actions/lib/utils/canModifyRecipients';
import createAttachments from 'owa-mail-compose-actions/lib/utils/createAttachments';
import {
    ComposeViewStateAdapter,
    createPluginViewStateAdapter,
} from '../utils/createPluginViewStateAdapter';
import deletePendingInlineAttachment from 'owa-mail-compose-actions/lib/utils/deletePendingInlineAttachment';
import { getRecipientWellsFromComposeViewState } from 'owa-mail-compose-actions/lib/utils/getAllRecipientsAsEmailAddressStrings';
import getCurrentFromAddress from 'owa-mail-compose-actions/lib/utils/getFromAddressFromRecipientWell';
import isSmartComposeEnabled from 'owa-mail-compose-actions/lib/utils/isSmartComposeEnabled';
import { LazyCortanaPickerPluginClass } from 'owa-cortana-compose';
import { LazyAugLoopPluginClass } from 'owa-editor-augloop-plugin';
import { LazyProofingPluginClass } from 'owa-editor-proofing-plugin';
import { LazyDictationPluginClass } from 'owa-editor-dictation-plugin';
import DockingPlugin from 'owa-editor-docking-plugin/lib/plugin/DockingPlugin';
import FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import addRecipientsToRecipientWell from 'owa-recipient-common/lib/actions/addRecipientsToRecipientWell';
import { getReadWriteRecipientViewStateFromFindRecipientPersonaType } from 'owa-recipient-create-viewstate/lib/util/getReadWriteRecipientViewStateFromFindRecipientPersonaType';
import removePersonaFromWell from 'owa-readwrite-recipient-well/lib/actions/removePersonaFromWell';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyForgottenAttachmentsPluginClass } from 'owa-editor-forgotten-attachments-plugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import { getAnnotationHandlerAsync as getUserDataAnnotationHandlerAsync } from 'owa-editor-user-data-handler';
import { getAnnotationHandlerAsync as getProofingAnnotationHandlerAsync } from 'owa-editor-proofing-handler';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazySmartSuggestionsPluginClass } from 'owa-editor-smart-suggestions-plugin';
import { LazySmartComposePluginClass } from 'owa-editor-smartcompose-plugin';
import { LazyTableEditPluginClass } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { LazyLinkPreviewPluginClass } from 'owa-link-preview-compose';
import { ComposeViewState, ComposeOperation, ExcludedPluginsForMail } from 'owa-mail-compose-store';
import {
    isBohemiaEnabledForScenario,
    BohemiaEnabledScenario,
    LazyPraguePluginClass,
} from 'owa-prague-integrations';
import getRecipientsFromRecipientWells from 'owa-recipient-common/lib/utils/getRecipientsFromRecipientWells';
import OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import InlineImagePlugin from 'owa-inline-image/lib/plugins/InlineImagePlugin';
import { LazyInlineImageResizePluginClass } from 'owa-inline-image-resize-plugin';
import { ReferenceAttachmentPlugin } from 'owa-editor-reference-attachment-plugin';
import isApprovalResponse from '../utils/isApprovalResponse';

function getSubjectHandler(adapter: ComposeViewStateAdapter): () => string {
    return () => adapter().subject;
}

export default function createComposePlugins(
    viewState: ComposeViewState
    //targetWindow: Window
): ExcludedPlugins<ExcludedPluginsForMail> {
    const viewStateAdapter = createPluginViewStateAdapter(viewState);

    const getAllRecipientsFunc = (adapter: ComposeViewStateAdapter) => () => {
        return getRecipientsFromRecipientWells(getRecipientWellsFromComposeViewState(adapter()));
    };

    const getAllRecipientWellsFunc = (adapter: ComposeViewStateAdapter) => () => {
        return getRecipientWellsFromComposeViewState(adapter());
    };

    const switchToReadWriteRecipientWell = (adapter: ComposeViewStateAdapter) => () => {
        if (viewState.showCompactRecipientWell) {
            switchToReadWriteRecipient(adapter());
        }
    };

    const userConfiguration: OwaUserConfiguration = getUserConfiguration();
    const isRtl = userConfiguration.SessionSettings.IsUserCultureRightToLeft;

    const addSelectedPersonaToWellWrapper = (newPersona: FindRecipientPersonaType) => {
        addRecipientsToRecipientWell(
            [getReadWriteRecipientViewStateFromFindRecipientPersonaType(newPersona)],
            viewState.toRecipientWell
        );
    };
    const removePersonaFromWellWrapper = (persona: FindRecipientPersonaType) => {
        removePersonaFromWell(viewState.toRecipientWell, persona);
    };

    return {
        docking: new DockingPlugin(
            createPluginViewStateAdapter(viewState, 'docking'),
            viewStateAdapter
        ),
        ribbon: new RibbonPlugin(
            createPluginViewStateAdapter(viewState, 'ribbon'),
            viewStateAdapter
        ),
        inlineImage: new InlineImagePlugin(
            createPluginViewStateAdapter(viewState, 'inlineImage'),
            viewStateAdapter,
            createPluginViewStateAdapter(viewState, 'attachmentWell'),
            (files, props) => createAttachments(files, viewStateAdapter(), props),
            placeholderId => deletePendingInlineAttachment(placeholderId, viewStateAdapter()),
            isApprovalResponse(viewState)
        ),
        inlineImageResize: new LazyInlineImageResizePluginClass(
            createPluginViewStateAdapter(viewState, 'inlineImageResize'),
            viewStateAdapter
        ),
        mentionsPicker: new LazyAtMentionsPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'mentionsPicker'),
            viewStateAdapter,
            addSelectedPersonaToWellWrapper,
            removePersonaFromWellWrapper,
            switchToReadWriteRecipientWell(viewStateAdapter),
            canModifyRecipients(viewState),
            getAllRecipientsFunc(viewStateAdapter)
        ),
        cortanaCompose: isFeatureEnabled('mc-cortanaCompose') && new LazyCortanaPickerPluginClass(),
        emojiPicker: new LazyEmojiPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'emojiPicker'),
            viewStateAdapter,
            true /* useFlexPane */,
            viewState.expressionId
        ),
        smartSuggestions: isFeatureEnabled('mc-smartSuggestions')
            ? new LazySmartSuggestionsPluginClass(
                  viewStateAdapter,
                  getAllRecipientWellsFunc(viewStateAdapter),
                  viewState.expressionId
              )
            : null,
        linkPreview: new LazyLinkPreviewPluginClass(),
        pasteOption: new LazyPasteOptionPluginClass(
            createPluginViewStateAdapter(viewState, 'pasteOption'),
            viewStateAdapter,
            isRtl
        ),
        tableEdit: new LazyTableEditPluginClass(
            createPluginViewStateAdapter(viewState, 'tableEdit'),
            viewStateAdapter
        ),
        link: new LazyLinkPluginClass(
            viewState,
            viewState.attachmentWell,
            getAllRecipientWellsFunc(viewStateAdapter),
            getCurrentFromAddress(viewState.toRecipientWell),
            false /* isCalendar */
            //targetWindow
        ),
        smartCompose: isSmartComposeEnabled()
            ? new LazySmartComposePluginClass(
                  createPluginViewStateAdapter(viewState, 'smartCompose'),
                  viewStateAdapter,
                  getAllRecipientWellsFunc(viewStateAdapter),
                  getSubjectHandler(viewStateAdapter),
                  viewState.operation
              )
            : null,
        prague: isBohemiaEnabledForScenario(BohemiaEnabledScenario.MailCompose)
            ? new LazyPraguePluginClass()
            : null,
        proofing: isFeatureEnabled('mc-officeEditorBX')
            ? new LazyProofingPluginClass(
                  viewState.operation === ComposeOperation.EditDraft ? 'Edit' : 'New'
              )
            : null,
        augLoop: isFeatureEnabled('mc-augmentationLoop')
            ? new LazyAugLoopPluginClass([
                  getUserDataAnnotationHandlerAsync,
                  getProofingAnnotationHandlerAsync,
              ])
            : null,
        dictation: isFeatureEnabled('cmp-dictation')
            ? new LazyDictationPluginClass(viewStateAdapter)
            : null,
        forgottenAttachments: new LazyForgottenAttachmentsPluginClass(
            createPluginViewStateAdapter(viewState, 'forgottenAttachments'),
            viewStateAdapter
        ),
        referenceAttachment: new ReferenceAttachmentPlugin(),
    };
}
